import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useScrollCtrl, IBounds } from 'app/components/scroll/ScrollContext';
import { useLatestCallback } from 'app/utils/react';
import { useStickyHeight } from 'app/components/layout/Page/StickyContext';

export interface IVisibilityOptions {
  /**
   * Proportion (0 to 1) of how much of the view needs to be visible
   * for the component to be considered visible (in horizontal direction)
   *
   * Defaults to 0 which means if it's considered visible as soon
   * as any part of it is visible.
   */
  minVisibleWidth?: number;

  /**
   * Like `minVisibleWidth` but in vertical direction
   */
  minVisibleHeight?: number;

  /**
   * Called when becomes visible. Called also when the element is
   * visible after mounting.
   */
  onVisible?: () => void;

  /**
   * Called when scrolled out of view or gone. Initially the element
   * is assumed obscured so this callback is not called if the
   * element is out of view when loaded
   */
  onObscured?: () => void;
}

export interface IVisibility {
  /**
   * Pass this as `ref` to the element you want to know visibility
   * of.
   */
  ref: (element: Element | null) => void,
  /**
   * Whether element is visible, it's always `false` on first render.
   */
  visible: boolean,
}

/**
 * Measures visibility of an element in relation to the scrolling
 * context
 */
export const useVisibility = ({
  minVisibleWidth = 0,
  minVisibleHeight = 0,
  onVisible = () => {},
  onObscured = () => {},
}: IVisibilityOptions): IVisibility => {
  const latestOnVisible = useLatestCallback(onVisible);
  const latestOnObscured = useLatestCallback(onObscured);
  const stickyHeight = useStickyHeight();

  const scroll = useScrollCtrl();
  const [visible, setVisible] = useState<boolean>(false);
  const ref = useRef<Element | null>(null);

  const onScroll = useCallback(() => {
    if (ref.current) {
      const scrollerBounds = scroll.getBounds();
      const elementBounds = ref.current.getBoundingClientRect();

      const visibleVertically = isVisible(
        getVerticalBounds({
          ...scrollerBounds,
          top: scrollerBounds.top + stickyHeight,
          height: scrollerBounds.height - stickyHeight,
        }),
        getVerticalBounds(elementBounds),
        minVisibleHeight,
      );

      const visibleHorizontally = isVisible(
        getHorizontalBounds(scrollerBounds),
        getHorizontalBounds(elementBounds),
        minVisibleWidth,
      );

      setVisible(visibleVertically && visibleHorizontally);
    } else {
      setVisible(false);
    }
  }, [minVisibleHeight, minVisibleWidth, scroll, stickyHeight]);

  const setRef = useCallback((element: Element | null) => {
    ref.current = element;
    onScroll();
  }, [ref, onScroll]);

  useEffect(() => {
    scroll.bindEvents({ scroll: onScroll });

    return () => {
      scroll.unbindEvents({ scroll: onScroll });
    };
  }, [scroll, onScroll]);

  // Call `onVisible` only on the `false->true` transition and `onObscured` only
  // on cleanup. This will guarantee that `onObscured` is called when the
  // component unmounted while being visible.
  useEffect(() => {
    if (visible) {
      latestOnVisible();
    }
  }, [visible, latestOnVisible]);

  useEffect(() => {
    if (visible) {
      return () => latestOnObscured();
    }
  }, [visible, latestOnObscured]);

  return useMemo(() => ({ ref: setRef, visible }), [setRef, visible]);
};

interface IBoundsRange {
  start: number;
  end: number;
  length: number;
}

const getVerticalBounds = ({ top, bottom, height }: IBounds): IBoundsRange => ({
  start:  top,
  end:    bottom,
  length: height,
});

const getHorizontalBounds = ({ left, right, width }: IBounds): IBoundsRange => ({
  start:  left,
  end:    right,
  length: width,
});

const isVisible = (scrollerBounds: IBoundsRange, elementBounds: IBoundsRange, minVisible: number): boolean => {
  const obscuredTopPx = scrollerBounds.start - elementBounds.start;
  const obscuredBottomPx = elementBounds.end - scrollerBounds.end;
  const allowedObscuredPx = elementBounds.length * (1 - minVisible);

  return (
    (obscuredTopPx <= allowedObscuredPx) &&
    (obscuredBottomPx <= allowedObscuredPx)
  );
};
